import { INCENTIVE_STATUS } from "@/constants";

// SDCP-1325: the three real sizing outcomes, plus a project whose sizing never ran.
export type SizingStatus = "passed" | "pending" | "failed" | "not_run";

/**
 * Type for form validation function values.
 * Represents the possible types a form field value can have before validation.
 */
export type FormValidationValue = string | number | undefined | null;

export type BudgetType = {
  createdAt: string;
  id: number;
  paidTotal: number;
  remainingTotal: number;
  reservedTotal: number;
  total: number;
  updatedAt: string;
};

export enum TransferActionType {
  paid = "paid",
  reserved = "reserved",
  remaining = "remaining",
}

export type TransactionType = {
  id: number;
  amount: number;
  transaction_type: string;
  type: "add" | "deduct" | "transfer";
  note: string | null;
  total: number;
  budgetId: number;
  applicationId: number | null;
  createdAt: string;
  updatedAt: string;
  BudgetId: number;
  ApplicationId: number | null;
  from: "add" | "deduct" | "transfer";
  to: "add" | "deduct" | "transfer";
  email: string;
};

export type TransactionsDataType = { [key: string]: TransactionType[] };

export interface MonthData {
  Reserved: number;
  Paid: number;
  Remaining: number;
}

export interface AnalysisData {
  January: MonthData;
  February: MonthData;
  March: MonthData;
  April: MonthData;
  May: MonthData;
  June: MonthData;
  July: MonthData;
  August: MonthData;
  September: MonthData;
  October: MonthData;
  November: MonthData;
  December: MonthData;
}

export interface UserType {
  hashedPassword: string;
  verificationCode: string;
  role: "administrator" | "contractor" | "oem";
  status:
    | "approved"
    | "pending_approval"
    | "suspended"
    | "passed_training"
    | "deleted";
  lastLogin: string;
  rememberMe: boolean;
  id: number;
  fullName: string;
  email: string;
  resetPasswordToken: string | null;
  trainingCompleted: boolean;
  avatar: string | null;
  failedAttempts: number;
  accountBlocked: string | null;
  manufactoringBrand: string;
  contactEmail: string;
  contactName: string;
  contactPhone: string;
  dermsList: string | null;
  apiIntegrations: string | null;
  ieeeProtocol: boolean;
  maintainingTelemetryDataInterval: string;
  achInformation: string;
  companyName: string | null;
  businessLicenseNumber: string | null;
  companyAddress: string | null;
  companyWebsite: string | null;
  welcomeMessage: string | null;
  termsAndConditions?: boolean;
  certification?: FileRecord | null;
  license?: FileRecord | null;
  createdAt: string;
  updatedAt: string;
  files?: FileRecord[];
  battery: Battery[];
}

interface UserBatteries {
  createdAt: string;
  updatedAt: string;
  batteryId: number;
  userId: number;
}

interface Battery {
  id: number;
  batteryModel: string;
  batteryType: string;
  batteryLocations: string;
  totalCapacityKwh: string;
  totalCapacityIncentiveKwh?: string;
  inverterModels: string | null;
  createdAt: string;
  updatedAt: string;
  UserBatteries: UserBatteries;
  totalCapacity?: string;
  nominalPower?: string;
  nominalPowerKw?: string;
}

export interface LogType {
  accountName: string;
  createdAt: string;
  endpoint: string;
  email: string;
  role: UserType["role"];
  id: number;
  ipAddress: string;
  location: string;
  message: string;
  note: string;
  status: string;
  timestamp: string;
  updatedAt: string;
  logType: string;
  userId?: number;
}

export interface SessionType {
  createdAt: string;
  id: number;
  ipAddress: string;
  location: string;
  status: string;
  updatedAt: string;
  user: number;
  userAgent: string;
}

export interface FileRecord {
  accountEmail: string;
  accountName: string | null;
  createdAt: string;
  deletedAt: string | null;
  fileName: string;
  id: number;
  key: string;
  mimeType: string;
  size: string;
  status: string;
  type: string;
  updatedAt: string;
  url: string;
  timeReviewd?: string;
  timeSubmited?: string;
  serialNumber: string | null;
  quarterlyPerformanceIncentiveValue: string | null;
  quarterName: string | null;
  performanceYear: number | null;
  userId: number;
}

export type LinkedProject = Pick<
  ProjectRecord,
  "id" | "customerName" | "incentiveStatus" | "upfrontIncentivePayment"
>;

export interface ProjectRecord {
  id: number;
  /** SDCP-1340: set on an add-on project (second project on the same account). */
  parentProjectId?: number | null;
  parentProject?: LinkedProject | null;
  addOnProjects?: LinkedProject[];
  userId: number;
  customerName: string;
  address: string;
  city: string;
  zipCode: string;
  incentiveStatus: INCENTIVE_STATUS | undefined;
  enrolmentStatus:
    | "new"
    | "active"
    | "pending"
    | "archived"
    | "correction_needed"
    | "rejected"
    | "contractor_updated"
    | null;
  batteryOem: string;
  batteryModel: string;
  batterySerialNumber?: string;
  upfrontIncentivePayment: string;
  batteryType: string;
  contractor: string | null;
  existingInverter: string;
  solarKwdc: number;
  solarKwac: number;
  solarAnnualProduction: number;
  newSolarKwdc: number;
  newSolarKwac: number;
  newSolarAnnualProduction: number;
  usableCapacity: string;
  usableCapacityIncentive?: string;
  numberOfBatteries: string;
  summerEnrolment: string;
  winterEnrolment: string;
  participation: "weekdays" | "weekends" | "weekdays_weekends";
  accountHash: string;
  accountEmail: string;
  payee?: string;
  payeeType?: string;
  payeeName?: string;
  payeeEmail?: string;
  mailingAddress?: string;
  mailingCity?: string;
  mailingState?: string;
  mailingZipCode?: string;
  Customer_First_Name?: string;
  Customer_Last_Name?: string;
  Mailing_Address?: string;
  Mailing_City?: string;
  Mailing_State?: string;
  Mailing_Zip_Code?: string;
  applicantState?: string;
  state?: string;

  ratePlan?: string;
  careEligible?: boolean;
  feraEligible?: boolean;
  cocEligible?: boolean;

  note: string | null;
  createdAt: string;
  updatedAt: string;
  approvalDate: string | null;
  deletedAt: string | null;
  participantEnrollment: FileRecord;
  contract: FileRecord;
  systemSizingTool: FileRecord;
  comment: NoteType;
  latestEnrolmentUpdate: string | null;
  latestIncentiveUpdate: string | null;
  oemId: number | null;
  contractorId: number | null;
  operationalizedDate?: string;
  estimated_installation_date?: string | null;
  ProjectExtensions?: ProjectExtensionRecord[];
  ProjectDocumentations?: FileRecord[];
  document_url?: string | null;
  sent_to_customer?: string | null;
  sizing_submitted?: number;
  sizing_passed?: number;
  sizing_status?: SizingStatus;
  nominalPowerkw?: string;
  ptoNameplateKw?: string;
  total_cost?: string | number;
  externalIncentives?: string | number;
  thirdPartyOwned?: boolean;
  externalIncentivePrograms?: string;
  inverterManufacturer?: string;
  inverterName?: string;
  complete?: boolean;
  projectSignature?: {
    size: string;
    [key: string]: any;
  };
  // Virtual Peaker fields
  billkey?: string | null;
  deviceId?: number | null;
  vpDeviceStatus?: string | null;
  vpDeviceStatusCheckedAt?: string | null;
}

export interface ProjectExtensionRecord {
  id: number;
  projectId: number;
  extendToDate: string;
  note: string;
  userId: number;
  createdAt: string;
  updatedAt: string;
}

export interface SdcpProjectData {
  NEM_SBP?: string;
  CARE?: string;
  FERA?: string;
  isCoC?: string;
  State?: string;
}

export interface ProjectApiResponse extends ProjectRecord {
  sdcpProject?: SdcpProjectData;
  totalCapacity?: string;
}

export type TemplateRowType = {
  id: number;
  fileName: string;
  fileSize: string;
  deletedAt: string | null;
  createdAt: string;
  fileType: string;
  accountName: string;
  accountEmail: string;
  url: string;
};

export type CookiesBody = {
  essentialCookies: true;
  performanceCookies?: boolean;
  functionalCookies?: boolean;
  targetingCookies?: boolean;
};

export type ArticleType = {
  id: number;
  title: string;
  content: string;
  createdAt: string;
  dateStamp: string;
};

export type NoteType = {
  id: number;
  note: string;
  userId: UserType["id"];
  projectId: ProjectRecord["id"];
  userName: string;
  createdAt: string;
  updatedAt: string;
};

export type FinanceRow = {
  id: number;
  customerName: string;
  accountEmail: string;
  address: string;
  city: string;
  applicantState: string;
  zipCode: string;
  contractor: string;
  payee: string;
  payeeType: string;
  ldcAccountNumber: string;
  nemSbp: string;
  nemStartDate?: string;
  sbpStartDate?: string;
  utilityRate?: string;
  utilityRateStartDate?: string;
  newSolarSystemSize: number;
  batteryOem: string;
  batteryModel: string;
  batterySerialNumber: string;
  numberOfBatteries: string;
  batteryNameplateKw: string;
  usableCapacity: string;
  usableCapacityIncentive?: string;
  ptoNameplateKw: string;
  ptoSolarKwAc: string;
  projectType: string;
  cocCareFera: string;
  upfrontIncentivePayment: string;
  total_cost: string;
  termsAndConditions: string;
  ptoLetterDate: string;
  ptoSubmittedDate: string;
  finance_review_date: string;
  note: string;
  batteryProduct?: string;
  solarKwdc?: number;
  solarKwac?: number;
  solarAnnualProduction?: number;
  newSolarKwdc?: number;
  newSolarKwac?: number;
  newSolarAnnualProduction?: number;
  incentiveStatus?: string;
  enrolmentStatus?: string;
  approvalDate?: string;
  createdAt?: string;
  updatedAt?: string;
  accountHash?: string;
  state?: string;
  [key: string]: any;
};

/**
 * Error response type for API error handling.
 * Used to safely extract error messages from caught exceptions.
 */
export interface ApiErrorResponse {
  response?: {
    data?: {
      error?: string;
      code?: string;
      message?: string;
    };
  };
}

/**
 * Type guard to check if an error is an ApiErrorResponse.
 * This avoids using 'as' type assertions.
 */
export function isApiErrorResponse(error: unknown): error is ApiErrorResponse {
  return (
    typeof error === "object" &&
    error !== null &&
    "response" in error &&
    (error.response === undefined ||
      (typeof error.response === "object" &&
        error.response !== null &&
        "data" in error.response &&
        (error.response.data === undefined ||
          typeof error.response.data === "object")))
  );
}

/**
 * Safely extracts an error message from an unknown error.
 * Returns the error message from the API response or a fallback message.
 * This avoids using 'as' type assertions.
 */
export function getApiErrorMessage(
  error: unknown,
  fallback: string = "An error occurred. Please try again."
): string {
  if (isApiErrorResponse(error)) {
    return error.response?.data?.error || fallback;
  }
  return fallback;
}
